import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// ═══════════════════════════════════════════════════════════════
// DECLARATION INPUT COMPONENT
// ═══════════════════════════════════════════════════════════════

interface DeclarationInputProps {
  minValue: number;      // Minimum declaration allowed
  maxValue: number;
  currentRoll: number;   // THIS round's dice total only
  haul: number;          // Coins already claimed (0 on first roll)
  onDeclare: (amount: number) => void;
  disabled?: boolean;
}

export const DeclarationInput: React.FC<DeclarationInputProps> = ({
  minValue,
  maxValue,
  currentRoll,
  haul,
  onDeclare,
  disabled = false,
}) => {

  // Initialize to haul + currentRoll, capped at maxValue
  const [value, setValue] = useState(Math.min(haul + currentRoll, maxValue));

  // Update value when bounds change
  useEffect(() => {
    setValue(Math.min(haul + currentRoll, maxValue));
  }, [haul, maxValue, currentRoll]);

  // Lying if declaration > currentRoll (you're claiming more than you rolled)
  const isLying = value > currentRoll;
  const isBelowMin = value < haul;

  const handleSubmit = () => {
    if (!disabled && !isBelowMin) {
      onDeclare(value);
    }
  };

  // Determine declaration status text and style
  const getDeclarationStatus = () => {
    if (isBelowMin) {
      return {
        text: `Must declare at least ${haul}`,
        className: 'bs-declaration-truth-lie',
      };
    }
    if (isLying) {
      return {
        text: 'Bluffing!',
        className: 'bs-declaration-truth-lie',
      };
    }
    return {
      text: 'Truthful',
      className: 'bs-declaration-truth-honest',
    };
  };

  const status = getDeclarationStatus();

  return (
    <div className="bs-declaration">
      <h3 className="bs-declaration-title">Declare Your Haul</h3>

      <div className="bs-declaration-value">{value}</div>

      <div className="bs-declaration-slider" style={{ width: '100%' }}>
        <input
          type="range"
          className="bs-slider"
          min={1}
          max={maxValue}
          value={value}
          onChange={(e) => setValue(Number(e.target.value))}
          disabled={disabled}
        />
        <div className="bs-declaration-range">
          <span>Min: {minValue}</span>
          <span>Max: {maxValue}</span>
        </div>
      </div>

      <div className={`bs-declaration-truth ${status.className}`}>
        {status.text}
      </div>

      <motion.button
        className="bs-btn bs-btn-primary"
        onClick={handleSubmit}
        disabled={disabled || isBelowMin}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        "The Sea grants me {value} coins!"
      </motion.button>
    </div>
  );
};

export default DeclarationInput;
